import { APP_INITIALIZER, Provider, Renderer2, RendererFactory2, RendererType2 } from '@angular/core';

/**
 * AB#79631 — re-routes inline style-ATTRIBUTE writes through the CSSOM so they survive a
 * nonce-based `style-src` (no 'unsafe-inline').
 *
 * A nonce/hash cannot cover inline style *attributes*, only <style> *elements*. Angular's
 * `writeDirectStyle` applies static/host style attributes via `renderer.setAttribute(el,
 * 'style', ...)` — e.g. @ng-bootstrap's modal backdrop host `style="z-index: 1055"`.
 *
 * We intercept those and apply the declarations via the CSSOM **property-by-property**
 * (`style.setProperty`). Only individual CSSOM property writes bypass the style-src check in
 * current browsers — a bulk `style.cssText = ...` write is still treated as a style-attribute
 * write and blocked. (This is the same per-property path Angular's own [ngStyle]/[style.x]
 * bindings use, which is why those already work under the nonce-only policy.)
 *
 * The decoration wraps the already-resolved RendererFactory2 (the animation factory when
 * BrowserAnimationsModule is present) inside an APP_INITIALIZER — no DI cycle, animations
 * intact.
 *
 * Note: <style> *elements* created at runtime (Angular ViewEncapsulation, PrimeNG 16, and
 * GTM-injected third-party widgets such as the Zendesk Web Widget) are nonced separately by
 * an inline nonced bootstrap script in `index.phtml`, which patches `document.createElement`
 * before the GTM loader runs.
 */

/**
 * Applies a `style="..."` declaration string onto an element's inline style via CSSOM
 * property writes (not policed by CSP), replacing any previous inline declarations.
 *
 * Limitation: declarations are split on `;`/`:`, so values that themselves contain a `;`
 * (e.g. a `data:` URI in `background`) are not handled — none occur in the renderer-applied
 * host styles this reroute targets (z-index/position/overflow).
 */
function applyStyleViaCssom(style: CSSStyleDeclaration, cssText: string): void {
  // Clear existing inline declarations first (CSSOM removeProperty is not CSP-policed) so
  // that updates which drop a property take effect.
  for (let i = style.length - 1; i >= 0; i--) {
    style.removeProperty(style.item(i));
  }
  if (!cssText) {
    return;
  }
  for (const declaration of cssText.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon === -1) {
      continue;
    }
    const property = declaration.slice(0, colon).trim();
    if (!property) {
      continue;
    }
    let value = declaration.slice(colon + 1).trim();
    let priority = '';
    const important = /\s*!important\s*$/i.exec(value);
    if (important) {
      value = value.slice(0, important.index).trim();
      priority = 'important';
    }
    style.setProperty(property, value, priority);
  }
}

export function cspStyleAttributeRerouteFactory(rendererFactory: RendererFactory2): () => void {
  return () => {
    // RendererFactory2 reuses renderer instances (the shared default renderer and the
    // per-component-type renderers), so guard against wrapping the same instance twice —
    // otherwise setAttribute would accumulate an ever-growing wrapper chain.
    const patched = new WeakSet<Renderer2>();
    const createRenderer = rendererFactory.createRenderer.bind(rendererFactory);
    rendererFactory.createRenderer = (host: unknown, type: RendererType2 | null): Renderer2 => {
      const renderer = createRenderer(host, type);
      if (patched.has(renderer)) {
        return renderer;
      }
      patched.add(renderer);
      const setAttribute = renderer.setAttribute.bind(renderer);
      renderer.setAttribute = (el: any, name: string, value: string, namespace?: string) => {
        const style = el && (el as ElementCSSInlineStyle).style;
        if (name === 'style' && !namespace && style) {
          applyStyleViaCssom(style, value ?? '');
          return;
        }
        setAttribute(el, name, value, namespace);
      };
      return renderer;
    };
  };
}

/**
 * Registers {@link cspStyleAttributeRerouteFactory} as an APP_INITIALIZER. Add to AppModule.
 */
export const CSP_STYLE_PROVIDER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: cspStyleAttributeRerouteFactory,
  deps: [RendererFactory2],
  multi: true,
};
